import { useState, useCallback, useRef, useEffect } from 'react';
import { useUIStore } from '@/stores/uiStore';

// Web Speech API types
interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
  interface SpeechRecognition extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    start(): void;
    stop(): void;
    abort(): void;
    onresult: ((event: SpeechRecognitionEvent) => void) | null;
    onerror: ((event: { error: string }) => void) | null;
    onend: (() => void) | null;
    onstart: (() => void) | null;
  }
}

export function useVoice() {
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const transcriptRef = useRef('');

  const { setIsListening, setIsSpeaking, setJarvisActivity } = useUIStore();

  const analyzeAudio = useCallback(() => {
    if (!analyserRef.current) return;
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);
    const sum = dataArray.reduce((a, b) => a + b, 0);
    const average = sum / dataArray.length;
    const normalizedLevel = Math.min(average / 128, 1);
    setAudioLevel(normalizedLevel);
    setJarvisActivity(0.3 + normalizedLevel * 0.5);
    animFrameRef.current = requestAnimationFrame(analyzeAudio);
  }, [setJarvisActivity]);

  const startRecording = useCallback(async () => {
    try {
      // Get mic for audio level visualization
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;

      audioContextRef.current = new AudioContext();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      // Start Web Speech API recognition
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        console.error('Speech recognition not supported');
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      transcriptRef.current = '';

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        // Collect both final and interim — short utterances often never
        // reach isFinal before the user taps stop, so if we only kept
        // finalized chunks, the transcript would be empty. Store the
        // best-available transcript (final takes precedence, interim as
        // fallback) so stopRecording + transcribeAudio always has text.
        let finalTranscript = '';
        let interimTranscript = '';
        for (let i = 0; i < event.results.length; i++) {
          const piece = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += piece;
          } else {
            interimTranscript += piece;
          }
        }
        const combined = (finalTranscript + ' ' + interimTranscript).trim();
        if (combined) {
          transcriptRef.current = combined;
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
      };

      recognition.start();
      recognitionRef.current = recognition;

      setIsRecording(true);
      setIsListening(true);
      analyzeAudio();
    } catch (err) {
      console.error('Failed to start recording:', err);
    }
  }, [setIsListening, analyzeAudio]);

  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      // Stop speech recognition
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }

      // Stop mic stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }

      setIsRecording(false);
      setIsListening(false);
      setAudioLevel(0);

      // Return a dummy blob — the actual text is in transcriptRef
      resolve(new Blob([], { type: 'audio/webm' }));
    });
  }, [setIsListening]);

  const transcribeAudio = useCallback(async (_audioBlob: Blob) => {
    setIsTranscribing(true);
    try {
      // Wait a beat for final recognition results
      await new Promise((r) => setTimeout(r, 300));

      const text = transcriptRef.current.trim();
      setTranscription(text);
      return text;
    } finally {
      setIsTranscribing(false);
    }
  }, []);

  const playAudio = useCallback(
    async (source: string | Blob) => {
      setIsSpeaking(true);
      setJarvisActivity(0.8);

      try {
        const url = source instanceof Blob ? URL.createObjectURL(source) : source;
        const audio = new Audio(url);

        const playbackContext = new AudioContext();
        const playbackSource = playbackContext.createMediaElementSource(audio);
        const playbackAnalyser = playbackContext.createAnalyser();
        playbackAnalyser.fftSize = 256;
        playbackSource.connect(playbackAnalyser);
        playbackAnalyser.connect(playbackContext.destination);

        const analyzePlayback = () => {
          const dataArray = new Uint8Array(playbackAnalyser.frequencyBinCount);
          playbackAnalyser.getByteFrequencyData(dataArray);
          const sum = dataArray.reduce((a, b) => a + b, 0);
          const average = sum / dataArray.length;
          setJarvisActivity(0.4 + (average / 128) * 0.6);
          if (!audio.paused && !audio.ended) {
            requestAnimationFrame(analyzePlayback);
          }
        };

        audio.onplay = () => analyzePlayback();
        audio.onended = () => {
          setIsSpeaking(false);
          setJarvisActivity(0.1);
          playbackContext.close();
          if (source instanceof Blob) URL.revokeObjectURL(url);
        };
        audio.onerror = () => {
          setIsSpeaking(false);
          setJarvisActivity(0);
          playbackContext.close();
        };
        await audio.play();
      } catch (err) {
        console.error('Audio playback failed:', err);
        setIsSpeaking(false);
        setJarvisActivity(0);
      }
    },
    [setIsSpeaking, setJarvisActivity],
  );

  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.abort();
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (audioContextRef.current) audioContextRef.current.close();
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  return {
    isRecording,
    transcription,
    isTranscribing,
    audioLevel,
    startRecording,
    stopRecording,
    transcribeAudio,
    playAudio,
  };
}
